'use client';

import { Calendar, Users, ArrowRight, AlertCircle } from 'lucide-react';
import type { ServingRole } from '@/types/serve-finder';
import { Img } from '@/lib/asset-url';

interface ServingRoleCardProps {
  role: ServingRole;
}

/** Horizontal sub-card for a serving role inside the event detail sheet */
export function ServingRoleCard({ role }: ServingRoleCardProps) {
  return (
    <a
      href={role.Sign_Up_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group border-border bg-background flex gap-3 border p-3 transition-shadow duration-200 hover:shadow-md sm:gap-4"
    >
      {/* Image — square on mobile, landscape on desktop */}
      {role.Image_URL && (
        <div className="h-20 w-20 shrink-0 overflow-hidden sm:h-24 sm:w-36">
          <Img
            src={role.Image_URL}
            alt={role.Title}
            className="h-full w-full object-cover transition-transform duration-[650ms] ease-out group-hover:scale-[1.025]"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <div>
          <h4 className="text-foreground text-sm leading-tight font-bold">{role.Title}</h4>
          <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-relaxed">
            {role.Description}
          </p>
          {role.Requirements && (
            <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600">
              <AlertCircle className="h-3 w-3 shrink-0" />
              {role.Requirements}
            </p>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            {role.Volunteers_Needed != null && (
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {role.Volunteers_Needed} needed
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {role.Time}
            </span>
          </div>

          <span className="flex items-center gap-1 text-xs font-semibold tracking-wide text-[var(--brand-secondary)] uppercase">
            Sign Up
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </a>
  );
}
